// 🔧 store_products.js
// ✅ Purpose: Manager-facing Store product catalog management page
// ✅ Features:
// - Uses useEventContext() to load the active event
// - Allows managers/admins to create Store products for an event
// - Saves products to /events/{eventId}/store_items
// - Supports product name, slug, description, image URL, category, display order
// - Supports active/featured flags
// - Supports pickup/shipping availability flags
// - Shows success and error notifications at the top for 20 seconds
// - Includes console logging through LGV logger
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db, storage, functions } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import imageCompression from "browser-image-compression";
import { deleteImageFromUrl } from "../../utils/storage_helpers";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";
import { httpsCallable } from "firebase/functions";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";

const StoreProducts = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const storeEnabled = hasPurchasedAddOn(
    eventDoc,
    "store"
  );

  const emptyForm = useMemo(
    () => ({
      name: "",
      productSlug: "",
      category: "",
      description: "",
      imageUrl: "",
      displayOrder: 100,
      active: true,
      featured: false,
      allowPickup: true,
      allowShipping: false,
      quantityAvailable: 0,
    }),
    []
  );

    const [products, setProducts] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);
    const [imageFile, setImageFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);

  // =====================================================
  // ⏱ Auto-clear success/error messages
  // =====================================================
  useEffect(() => {
    if (!status && !error) return;

    const timer = setTimeout(() => {
      setStatus(null);
      setError(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status, error]);

  // =====================================================
  // 🔤 Slug helper
  // =====================================================
  // ✅ Purpose:
  // - Creates clean URL-safe product slugs
  // - Used later for /:eventslug/store/:productSlug
  // =====================================================
  const buildSlug = (value) =>
    String(value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "");

  const formatPriceRange = (product) => {
    const min = Number(product.minPriceCents ?? product.priceCents ?? 0);
    const max = Number(product.maxPriceCents ?? min);

    if (min <= 0 && max <= 0) return "Price not set";
    if (min === max) return `$${(min / 100).toFixed(2)}`;
    return `$${(min / 100).toFixed(2)} - $${(max / 100).toFixed(2)}`;
  };

  // =====================================================
  // 📦 Load Store products
  // =====================================================
  const loadProducts = async () => {
    if (!eventId) return;

    setLoading(true);

    try {
      logger.log("🛒 Loading Store products", {
        eventId,
      });

      const productsQuery = query(
        collection(db, "events", eventId, "store_items"),
        orderBy("displayOrder", "asc")
      );

      const snap = await getDocs(productsQuery);

      const list = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setProducts(list);

      logger.log("✅ Store products loaded", {
        eventId,
        count: list.length,
      });
    } catch (err) {
      logger.error("❌ Failed loading Store products:", {
        value: err,
      });

      setProducts([]);
      setError("Unable to load Store products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready || !eventId) return;

    loadProducts();
  }, [ready, eventId]);

  // =====================================================
  // ✏️ Handle form changes
  // =====================================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // ✅ Auto-create slug from product name only when not manually editing slug
      if (name === "name" && !editingId) {
        updated.productSlug = buildSlug(value);
      }

      if (name === "productSlug") {
        updated.productSlug = buildSlug(value);
      }

      return updated;
    });
  };

  // =====================================================
    // 🖼 Upload Store product image
    // =====================================================
    // ✅ Purpose:
    // - Compresses product images before upload
    // - Uploads to Firebase Storage
    // - Shows upload progress like settings_images.js
    // - Returns public image URL for store_items.imageUrl
    // =====================================================
    const uploadStoreProductImage = async (file, productIdOrSlug) => {
    const ext = file.name.split(".").pop() || "jpg";
    const safeName = buildSlug(productIdOrSlug || form.productSlug || form.name);
    const refPath = `eventAssets/${eventId}/store_products/${safeName}.${ext}`;
    const storageRef = ref(storage, refPath);

    const compressed = await imageCompression(file, {
        maxWidthOrHeight: 1600,
        useWebWorker: true,
    });

    const uploadTask = uploadBytesResumable(storageRef, compressed);

    return new Promise((resolve, reject) => {
        uploadTask.on(
        "state_changed",
        (snapshot) => {
            const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );

            setUploadProgress(percent);
        },
        (uploadError) => reject(uploadError),
        async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
        }
        );
    });
    };

  // =====================================================
  // 💾 Save product
  // =====================================================
  const handleSave = async (e) => {
    e.preventDefault();

    setStatus(null);
    setError(null);

    const cleanedName = String(form.name || "").trim();
    const cleanedSlug = buildSlug(form.productSlug || form.name);

    if (!cleanedName) {
      setError("Product name is required.");
      return;
    }

    if (!cleanedSlug) {
      setError("Product slug is required.");
      return;
    }

    setSaving(true);

    try {

        let finalImageUrl = String(form.imageUrl || "").trim();

        if (imageFile) {
        if (form.imageUrl) {
            await deleteImageFromUrl(form.imageUrl);
        }

        finalImageUrl = await uploadStoreProductImage(
            imageFile,
            cleanedSlug
        );
        }

      const payload = {
        name: cleanedName,
        category: String(form.category || "").trim(),
        description: String(form.description || "").trim(),
        imageUrl: finalImageUrl,
        displayOrder: Number(form.displayOrder || 100),
        active: form.active === true,
        featured: form.featured === true,
        allowPickup: form.allowPickup === true,
        allowShipping: form.allowShipping === true,

        updatedAt: serverTimestamp(),
      };

      let productId = editingId;
      if (editingId) {
        await updateDoc(
          doc(db, "events", eventId, "store_items", editingId),
          payload
        );

        logger.log("✅ Store product updated", {
          eventId,
          productId: editingId,
        });

        setStatus("✅ Store product updated successfully.");
      } else {
        const created = await addDoc(
          collection(db, "events", eventId, "store_items"),
          {
            ...payload,
            hasVariants: false,
            totalAvailableInventory: 0,
            totalReceivedInventory: 0,
            totalSoldInventory: 0,
            totalReservedInventory: 0,
            createdAt: serverTimestamp(),
          }
        );
        productId = created.id;

        logger.log("✅ Store product created", {
          eventId,
        });

        setStatus("✅ Store product created successfully.");
      }
      const slugResult = await httpsCallable(functions, "updatePowerupSlug")({ eventId, family: "product", entityId: productId, slug: cleanedSlug || undefined });
      setForm((previous) => ({ ...previous, productSlug: slugResult.data.slug }));

      setForm(emptyForm);
      setEditingId("");

        setImageFile(null);
        setUploadProgress(0);

        if (fileInputRef.current) {
        fileInputRef.current.value = "";
        }

      await loadProducts();
    } catch (err) {
      logger.error("❌ Failed saving Store product:", {
        value: err,
      });

      setError("Unable to save Store product.");
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  // =====================================================
  // ✏️ Edit product
  // =====================================================
  const startEdit = (product) => {
    setEditingId(product.id);

    setForm({
      name: product.name || "",
      productSlug: product.productSlug || "",
      category: product.category || "",
      description: product.description || "",
      imageUrl: product.imageUrl || "",
      displayOrder: product.displayOrder ?? 100,
      active: product.active !== false,
      featured: product.featured === true,
      allowPickup: product.allowPickup !== false,
      allowShipping: product.allowShipping === true,
      quantityAvailable: Number(product.totalAvailableInventory || 0),
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // ❌ Cancel edit
  // =====================================================
  const cancelEdit = () => {
    setEditingId("");
    setForm(emptyForm);
    setStatus(null);
    setError(null);
  };


    // =====================================================
    // 🗑 Remove product image
    // =====================================================
    // ✅ Purpose:
    // - Deletes the current product image from Firebase Storage
    // - Clears store_items.imageUrl
    // - Keeps product record intact
    // =====================================================
    const handleRemoveImage = async () => {
    if (!editingId || !form.imageUrl) {
        setError("Save or select a product before removing an image.");
        return;
    }

    if (!window.confirm("Remove this product image?")) return;

    setStatus(null);
    setError(null);

    try {
        await deleteImageFromUrl(form.imageUrl);

        await updateDoc(
        doc(db, "events", eventId, "store_items", editingId),
        {
            imageUrl: "",
            updatedAt: serverTimestamp(),
        }
        );

        setForm((prev) => ({
        ...prev,
        imageUrl: "",
        }));

        setStatus("✅ Product image removed.");
        await loadProducts();
    } catch (err) {
        logger.error("❌ Failed removing Store product image:", {
        value: err,
        });

        setError("Unable to remove product image.");
    }
    };

  // =====================================================
  // 🗑 Delete product
  // =====================================================
  const handleDelete = async (product) => {
    const confirmed = window.confirm(
      `Delete "${product.name}"? This only removes the product catalog record.`
    );

    if (!confirmed) return;

    setStatus(null);
    setError(null);

    try {
      await deleteDoc(
        doc(db, "events", eventId, "store_items", product.id)
      );

      logger.log("🗑 Store product deleted", {
        eventId,
        productId: product.id,
      });

      setStatus("✅ Store product deleted.");
      await loadProducts();
    } catch (err) {
      logger.error("❌ Failed deleting Store product:", {
        value: err,
      });

      setError("Unable to delete Store product.");
    }
  };

  if (
    ready &&
    eventDoc &&
    !storeEnabled
  ) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Event Merch Store Add-on Required"
        description="Purchase the Event Merch Store add-on before managing Store products."
      />
    );
  }

  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Store Products..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        {status && (
          <div className="alert alert-success">
            {status}
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">
              🛒 Store Products
            </h1>

            <p className="lead text-muted mb-0">
              Manage Store products for {eventDoc?.name || "this event"}.
            </p>
          </div>
          <div className="col-12 col-lg-auto mt-3 mt-lg-0">
            <PromotionSourceButton source="store" sourceScope="collection" />
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white">
            <strong>
              {editingId ? "✏️ Edit Product" : "➕ Add Product"}
            </strong>
          </div>

          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">
                    Product Name *
                  </label>

                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Event T-Shirt"
                    required
                  />
                </div>

                {editingId && (
                  <div className="col-12 col-md-6">
                    <label className="form-label">Quantity Available</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.quantityAvailable}
                      readOnly
                    />
                    <div className="form-text">
                      Variant inventory is the source of truth. Update it from Variants.
                    </div>
                  </div>
                )}

                <div className="col-12 col-md-6">
                  <label className="form-label">
                    Product Slug *
                  </label>

                  <input
                    type="text"
                    name="productSlug"
                    className="form-control"
                    value={form.productSlug}
                    onChange={handleChange}
                    placeholder="event-t-shirt"
                    required
                  />

                  <div className="form-text">
                    Future public URL: /{eventslug}/store/{form.productSlug || "product-slug"}
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">
                    Category
                  </label>

                  <input
                    type="text"
                    name="category"
                    className="form-control"
                    value={form.category}
                    onChange={handleChange}
                    placeholder="Shirts, Hats, Stickers"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">
                    Display Order
                  </label>

                  <input
                    type="number"
                    name="displayOrder"
                    className="form-control"
                    value={form.displayOrder}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                <div className="col-12">
                <label className="form-label">
                    Product Image
                </label>

                {form.imageUrl && (
                    <div className="text-center border rounded bg-light p-3 mb-3">
                    <img
                        src={form.imageUrl}
                        alt={form.name || "Store product"}
                        className="img-fluid"
                        style={{
                        maxHeight: "220px",
                        objectFit: "contain",
                        }}
                    />

                    {editingId && form.imageUrl && (
                    <button
                        type="button"
                        className="btn btn-outline-danger btn-sm mt-3"
                        onClick={handleRemoveImage}
                    >
                        🗑 Remove Product Image
                    </button>
                    )}

                    </div>
                )}

                <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    ref={fileInputRef}
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                />

                <div className="form-text">
                    Upload a product image. Images are compressed before saving.
                </div>

                {saving && uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mt-3">
                    <label className="form-label fw-bold">
                        Upload Progress
                    </label>

                    <div className="progress">
                        <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        >
                        {uploadProgress}%
                        </div>
                    </div>
                    </div>
                )}
                </div>

                <div className="col-12">
                  <label className="form-label">
                    Description
                  </label>

                  <textarea
                    name="description"
                    className="form-control"
                    rows={4}
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Describe this product."
                  />
                </div>

                <div className="col-12">
                  <div className="row g-3">
                    <div className="col-12 col-md-6 col-xl-3">
                      <div className="form-check border rounded p-3 h-100">
                        <input
                          type="checkbox"
                          name="active"
                          id="store-product-active"
                          className="form-check-input"
                          checked={form.active}
                          onChange={handleChange}
                        />

                        <label
                          className="form-check-label fw-bold"
                          htmlFor="store-product-active"
                        >
                          Active
                        </label>

                        <div className="form-text">
                          Active products can appear publicly.
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-3">
                      <div className="form-check border rounded p-3 h-100">
                        <input
                          type="checkbox"
                          name="featured"
                          id="store-product-featured"
                          className="form-check-input"
                          checked={form.featured}
                          onChange={handleChange}
                        />

                        <label
                          className="form-check-label fw-bold"
                          htmlFor="store-product-featured"
                        >
                          Featured
                        </label>

                        <div className="form-text">
                          Featured products can appear on Event Home later.
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-3">
                      <div className="form-check border rounded p-3 h-100">
                        <input
                          type="checkbox"
                          name="allowPickup"
                          id="store-product-pickup"
                          className="form-check-input"
                          checked={form.allowPickup}
                          onChange={handleChange}
                        />

                        <label
                          className="form-check-label fw-bold"
                          htmlFor="store-product-pickup"
                        >
                          Pickup
                        </label>

                        <div className="form-text">
                          Product can be picked up at the event.
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-3">
                      <div className="form-check border rounded p-3 h-100">
                        <input
                          type="checkbox"
                          name="allowShipping"
                          id="store-product-shipping"
                          className="form-check-input"
                          checked={form.allowShipping}
                          onChange={handleChange}
                        />

                        <label
                          className="form-check-label fw-bold"
                          htmlFor="store-product-shipping"
                        >
                          Shipping
                        </label>

                        <div className="form-text">
                          Product can be shipped later.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingId
                    ? "💾 Save Product"
                    : "➕ Add Product"}
                </button>

                {editingId && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={cancelEdit}
                  >
                    Cancel Edit
                  </button>
                )}

                <Link
                  to={`/${eventslug}/store`}
                  className="btn btn-outline-dark"
                >
                  View Public Store
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
            <div>
              <strong>📦 Product Catalog</strong>
              <div className="small text-muted">
                Pricing and inventory are managed through each product&apos;s variants.
              </div>
            </div>

            <span className="badge bg-dark align-self-start align-self-md-center">
              {products.length} Product{products.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="card-body">
            {products.length === 0 ? (
              <div className="alert alert-info mb-0">
                No Store products have been added yet.
              </div>
            ) : (
              <div className="row g-4">
                {products.map((product) => (
                  <div key={product.id} className="col-12 col-md-6 col-xl-4">
                    <div className="card h-100 border shadow-sm">
                      <div className="card-body d-flex flex-column">
                        <div
                          className="d-flex align-items-center justify-content-center bg-light border rounded mb-3"
                          style={{
                            minHeight: "150px",
                          }}
                        >
                          {product.imageUrl ? (
                            <img
                              src={product.imageUrl}
                              alt={product.name || "Store product"}
                              className="img-fluid"
                              style={{
                                maxHeight: "135px",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <div className="text-muted small">
                              No Image
                            </div>
                          )}
                        </div>

                        <h5 className="fw-bold mb-1">
                          {product.name || "Unnamed Product"}
                        </h5>

                        <div className="small text-muted mb-2">
                          /{product.productSlug || product.id}
                        </div>

                        <div className="mb-3">
                          {product.category && (
                            <span className="badge bg-secondary me-2">
                              {product.category}
                            </span>
                          )}

                          {product.active !== false ? (
                            <span className="badge bg-success me-2">
                              Active
                            </span>
                          ) : (
                            <span className="badge bg-secondary me-2">
                              Hidden
                            </span>
                          )}

                          {product.featured && (
                            <span className="badge bg-warning text-dark me-2">
                              Featured
                            </span>
                          )}
                        </div>

                        {product.description && (
                          <p className="text-muted small">
                            {product.description}
                          </p>
                        )}

                        <div className="border rounded bg-light p-3 small mb-3">
                          <div>
                            <strong>Price:</strong>{" "}
                            {formatPriceRange(product)}
                          </div>

                          <div>
                            <strong>Pickup:</strong>{" "}
                            {product.allowPickup ? "Yes" : "No"}
                          </div>

                          <div>
                            <strong>Shipping:</strong>{" "}
                            {product.allowShipping ? "Yes" : "No"}
                          </div>

                          <div>
                            <strong>Quantity Available:</strong>{" "}
                            {Number(product.totalAvailableInventory || 0)} /{" "}
                            {Number(product.totalReceivedInventory || 0)}
                          </div>
                          <div>
                            <strong>Reserved:</strong>{" "}
                            {Number(product.totalReservedInventory || 0)}
                          </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 mt-auto">
                          <PromotionSourceButton source="store" sourceId={product.id} className="btn btn-outline-primary btn-sm" />
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => startEdit(product)}
                          >
                            Edit
                          </button>

                            <Link
                            to={`/${eventslug}/store_variants/${product.productSlug || product.id}`}
                            className="btn btn-outline-success btn-sm"
                            >
                            Variants
                            </Link>

                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(product)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-4">
          <Link
            to={`/${eventslug}/event`}
            className="btn btn-outline-secondary"
          >
            ← Back to Event Home
          </Link>
        </div>
      </div>
    </section>
  );
};

export default StoreProducts;
